<soft-page>
  @if (
    {
      item: (item$ | async),
      price: (price$ | async),
      stock: (stock$ | async),
      details: (details$ | async),
      pricelist: (pricelist$ | async),
      config: (config$ | async),
    };
    as data
  ) {
    <div class="flex flex-col gap-4">
      <div class="flex flex-col md:flex-row flex-wrap gap-3">
        <section class="self-center md:self-start">
          @if ($safeNavigationMigration(data?.item?.thumbnail) | media; as thumbnail) {
            @if (data?.item?.thumbnail && thumbnail) {
              <div
                class="w-56 h-56 rounded-2xl overflow-hidden border border-solid bg-surface-tertiary cursor-pointer shrink-0"
                (click)="openImageDialog(thumbnail)"
              >
                <img
                  [src]="thumbnail"
                  class="w-full h-full object-cover"
                  alt="thumbnail"
                />
              </div>
            }
          } @else {
            <div
              class="w-56 h-56 rounded-2xl border border-solid bg-surface-tertiary flex flex-col items-center justify-center shrink-0"
            >
              <soft-icon name="fa-regular fa-image-slash" size="lg" />
              <span class="ds-caption" style="color: var(--ds-text-muted)">
                {{ '#ARTIKELINFO.PAGES.DETAIL.MEDIA.NO_IMAGE' | translate }}
              </span>
            </div>
          }
        </section>

        <section class="grid grid-col-1 md:grid-cols-2 gap-2 flex-1 min-w-0">
          <div class="flex flex-col gap-1 bg-surface-tertiary rounded-lg px-3 py-2">
            <span class="text-xs font-semibold text-light uppercase tracking-wide">
              {{ '#ARTIKELINFO.PAGES.DETAIL.ITEM_NUMBER' | translate }}
            </span>
            <div class="flex items-center gap-1.5">
              <span class="font-bold text-sm">{{ data.item?.number }}</span>
              <soft-icon
                name="fa-regular fa-copy"
                class="cursor-pointer text-light hover:text-primary"
                (click)="copyItemNumberToClipboard(data.item?.number)"
                [softToolTip]="
                  '#ARTIKELINFO.PAGES.DETAIL.HEADER.COPY_ITEM_NUMBER' | translate
                "
              />
            </div>
          </div>
          <div class="flex flex-col gap-1 bg-surface-tertiary rounded-lg px-3 py-2">
            <span class="text-xs font-semibold text-light uppercase tracking-wide">
              {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.EAN' | translate }}
            </span>
            <span class="font-bold text-sm">
              {{
                data.item?.ean?.length
                  ? data.item?.ean
                  : ('#ARTIKELINFO.PAGES.DETAIL.HEADER.NOT_SPECIFIED' | translate)
              }}
            </span>
          </div>
          <div class="flex flex-col gap-1 bg-surface-tertiary rounded-lg px-3 py-2">
            <span class="text-xs font-semibold text-light uppercase tracking-wide">
              {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.MATCHCODE' | translate }}
            </span>
            <span class="font-bold text-sm">
              {{
                data.item?.matchcode?.length
                  ? data.item?.matchcode
                  : ('#ARTIKELINFO.PAGES.DETAIL.HEADER.NOT_SPECIFIED' | translate)
              }}
            </span>
          </div>
          @for (title of TITLES; track $index) {
            @if (data.item?.['title' + ($index + 1)]) {
              <div class="flex flex-col gap-1 bg-surface-tertiary rounded-lg px-3 py-2">
                <span class="text-xs font-semibold text-light uppercase tracking-wide">
                  {{ '#ARTIKELINFO.PAGES.DETAIL.ARTBEZ' + ($index + 1) | translate }}
                </span>
                <span class="font-bold text-sm">
                  {{ data.item?.['title' + ($index + 1)] }}
                </span>
              </div>
            }
          }
        </section>
      </div>

      <section>
        <div class="grid grid-cols-2 gap-3">
          @if (!data.pricelist) {
            <soft-ds-card density="compact" class="flex flex-col gap-2">
              <soft-ds-loading-skeleton
                width="50%"
                height="0.75rem"
                radius="var(--ds-radius-md)"
              />
              <soft-ds-loading-skeleton
                width="70%"
                height="1.125rem"
                radius="var(--ds-radius-md)"
              />
              <soft-ds-loading-skeleton
                width="40%"
                height="0.75rem"
                radius="var(--ds-radius-md)"
              />
            </soft-ds-card>
          } @else if (data.pricelist?.tieredPricelist?.[0]; as priceEntry) {
            <soft-ds-card density="compact" class="flex flex-col gap-1">
              <div class="flex items-center gap-1.5 text-light">
                <soft-icon name="fa-regular fa-tag" />
                <span class="text-xs font-semibold uppercase tracking-wide">
                  {{ priceEntry.priceLevel?.title }}
                </span>
              </div>
              @if (priceEntry.tieredPrices?.[0]; as tierPrice) {
                <span class="font-bold text-lg">
                  {{
                    tierPrice.gross
                      | currency: tierPrice.currency : 'symbol-narrow' : '1.2-2'
                  }}
                </span>
                <span class="text-xs text-light">
                  {{
                    '#ARTIKELINFO.PAGES.DETAIL.HEADER.PRICE_INCLUSIVE'
                      | translate
                      | interpolate: { unit: tierPrice.unit.name }
                  }}
                </span>
              } @else {
                <span class="font-bold text-lg">
                  {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.NO_VALUE' | translate }}
                </span>
              }
            </soft-ds-card>
          } @else {
            <soft-ds-card density="compact" class="flex flex-col gap-1">
              <div class="flex items-center gap-1.5 text-light">
                <soft-icon name="fa-regular fa-tag" />
                <span class="text-xs font-semibold uppercase tracking-wide">
                  {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.PRICE' | translate }}
                </span>
              </div>
              <span class="font-bold text-lg">
                {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.NO_VALUE' | translate }}
              </span>
            </soft-ds-card>
          }

          @if (!data.stock) {
            <soft-ds-card density="compact" class="flex flex-col gap-2">
              <soft-ds-loading-skeleton
                width="50%"
                height="0.75rem"
                radius="var(--ds-radius-md)"
              />
              <soft-ds-loading-skeleton
                width="70%"
                height="1.125rem"
                radius="var(--ds-radius-md)"
              />
              <soft-ds-loading-skeleton
                width="40%"
                height="0.75rem"
                radius="var(--ds-radius-md)"
              />
            </soft-ds-card>
          } @else if (data.stock; as stock) {
            @let unit = stock.stockAmount?.[0]?.itemUnit?.name;
            @let shown = stock.own ?? stock;
            <soft-ds-card density="compact" class="flex flex-col gap-1">
              <div class="flex items-center gap-1.5 text-light">
                <soft-icon name="fa-regular fa-warehouse" />
                <span class="text-xs font-semibold uppercase tracking-wide">
                  {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.AVAILABLE_STOCK' | translate }}
                </span>
              </div>
              <span
                class="text-xs text-light"
                data-testid="artikelinfo-header-stock-scope"
              >
                @if (stock.own) {
                  {{ stock.own.stock?.title || stock.own.stock?.name }}
                } @else {
                  {{
                    '#ARTIKELINFO.PAGES.DETAIL.HEADER.TOTAL_STOCK'
                      | translate
                      | interpolate: { count: stock.stockAmount?.length }
                  }}
                }
              </span>
              @if (shown.amount !== null && shown.amount !== undefined) {
                <span
                  class="font-bold text-lg"
                  data-testid="artikelinfo-header-stock"
                  [style.color]="
                    shown.amount > 0
                      ? 'var(--ds-text-status-success)'
                      : 'var(--ds-text-status-danger)'
                  "
                >
                  {{ shown.amount | number: '1.2-2' }}
                  {{ unit }}
                </span>
                @let dispo = shown.amount - (shown.reserved ?? 0) + (shown.orders ?? 0);
                <span class="text-xs text-light">
                  {{
                    (stock.own
                      ? '#ARTIKELINFO.PAGES.DETAIL.STOCK.DISPO'
                      : '#ARTIKELINFO.PAGES.DETAIL.STOCK.TOTAL_DISPO'
                    ) | translate
                  }}:
                  <span class="font-semibold" [class.text-danger]="dispo < 0">
                    {{ dispo | number: '1.2-2' }}
                    {{ unit }}
                  </span>
                </span>
                @if (stock.own) {
                  <span class="flex items-center gap-1.5 text-xs text-light">
                    <soft-ds-dot variant="info" size="sm" />
                    {{
                      '#ARTIKELINFO.PAGES.DETAIL.HEADER.TOTAL_STOCK'
                        | translate
                        | interpolate: { count: stock.stockAmount?.length }
                    }}: {{ stock.amount | number: '1.2-2' }} {{ unit }}
                  </span>
                }
              } @else {
                <span class="font-bold text-lg">
                  {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.NO_VALUE' | translate }}
                </span>
              }
            </soft-ds-card>
          } @else {
            <soft-ds-card density="compact" class="flex flex-col gap-1">
              <div class="flex items-center gap-1.5 text-light">
                <soft-icon name="fa-regular fa-warehouse" />
                <span class="text-xs font-semibold uppercase tracking-wide">
                  {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.AVAILABLE_STOCK' | translate }}
                </span>
              </div>
              <span class="font-bold text-lg">
                {{ '#ARTIKELINFO.PAGES.DETAIL.HEADER.NO_VALUE' | translate }}
              </span>
            </soft-ds-card>
          }
        </div>
      </section>

      @if (
        data?.pricelist &&
        data?.pricelist?.tieredPricelist &&
        (data?.pricelist?.tieredPricelist?.length ?? 0) > 0
      ) {
        <soft-price-list [data]="data.pricelist" />
      }

      @if (!data?.config?.hideListung) {
        <soft-listung
          [data]="data.details"
          [expanded]="data?.config?.listungExpanded ?? true"
        />
      }

      @if (!data?.config?.hideLagerstaende) {
        @if (data?.stock; as stock) {
          <soft-lagerstaende
            [data]="stock"
            [expanded]="data?.config?.lagerstaendeExpanded ?? true"
            [showNextOrderDate]="data?.config?.showNextOrderDate || false"
            (showBewegungen)="showBewegungen($event.stockLocation, data.item)"
          />
        }
      }

      @if (data?.details; as details) {
        <soft-details
          [data]="details"
          [hideLagerplaetze]="data?.config?.hideLagerplaetze || false"
          [hideEinheiten]="data?.config?.hideEinheiten || false"
          [hideLieferanten]="data?.config?.hideLieferanten || false"
          [lagerplaetzeExpanded]="data?.config?.lagerplaetzeExpanded ?? true"
          [einheitenExpanded]="data?.config?.einheitenExpanded ?? true"
          [lieferantenExpanded]="data?.config?.lieferantenExpanded ?? true"
        />
      }

      @if (!data?.config?.hideOffeneBestellungen) {
        @if (data?.stock; as stock) {
          @if ((stock?.orders || 0) > 0) {
            <soft-ds-card density="compact">
              <div class="flex flex-col gap-3">
                <h6
                  class="font-semibold text-light text-sm uppercase leading-wide tracking-wide"
                >
                  {{ '#ARTIKELINFO.PAGES.DETAIL.OPEN_ORDERS.TITLE' | translate }}
                </h6>
                @for (stockEntry of stock.stockAmount; track stockEntry) {
                  @if (stockEntry?.orders && (stockEntry?.orders || 0) > 0) {
                    <soft-ds-card class="flex flex-col gap-3">
                      <div class="flex items-center gap-3">
                        <div
                          class="border border-primary text-primary rounded-lg p-3 shrink-0 bg-primary-50 hover:bg-primary-100 hover:cursor-pointer"
                          (click)="showBewegungen(stockEntry, data.item)"
                        >
                          <soft-icon name="fa-regular fa-clipboard-list" />
                        </div>
                        <span class="font-semibold text-sm leading-snug flex-1 min-w-0">
                          {{ stockEntry.stock?.title || stockEntry.stock?.name }}
                        </span>
                      </div>
                      <hr class="border-separator" />
                      <div class="flex items-center gap-2 text-light text-xs">
                        <soft-icon name="fa-regular fa-box-open" />
                        <span>
                          {{
                            '#ARTIKELINFO.PAGES.DETAIL.OPEN_ORDERS.OPEN_AMOUNT'
                              | translate
                          }}:
                        </span>
                        <span class="font-bold ml-auto">
                          {{ stockEntry.orders | number: '1.2-2' }}
                        </span>
                      </div>
                    </soft-ds-card>
                  }
                }
              </div>
            </soft-ds-card>
          }
        }
      }

      @if (!data?.config?.hideUmsatzabfrage) {
        @if (umsaetze$ | async; as umsaetze) {
          @if (params()?.showUmsatzabfrage) {
            <soft-umsatzabfrage
              [data]="umsaetze"
              [umsatzabfrageFilialen]="umsaztabfrageFilialen$ | async"
              [selectedUmsatzabfrageViewIndex]="selectedUmsatzabfrageViewIndex$ | async"
              [item]="item$ | async"
              [sum]="sum$ | async"
              [umsatzabfrageForm]="umsatzabfrageForm"
              (formSubmit)="onSubmit(data.item)"
              (tabIndexChanged)="onTabIndexChanged($event)"
            />
          }
        }
      }
    </div>
  }
</soft-page>
<ng-template #menuBarTemplate>
  <div class="flex items-center gap-1.5">
    <soft-menu-bar-button
      [icon]="'fa-regular fa-heart'"
      [label]="'#ARTIKELINFO.PAGES.DETAIL.ACTIONS.ADD_FAVORITE' | translate"
      [tooltip]="'#ARTIKELINFO.PAGES.DETAIL.ACTIONS.ADD_FAVORITE' | translate"
      [variant]="'primary'"
      (execute)="addFavorite()"
    />
    <soft-menu-bar-button
      [icon]="'fa-regular fa-barcode-read'"
      [label]="'#ARTIKELINFO.PAGES.DETAIL.ACTIONS.SCAN' | translate"
      [tooltip]="'#ARTIKELINFO.PAGES.DETAIL.ACTIONS.SCAN' | translate"
      [variant]="'outline'"
      (execute)="scan()"
    />
  </div>
</ng-template>
